/**
 * How a live call's words build up as the frames arrive. The provider sends
 * fragments (OpenAI's deltas, Gemini's incremental transcription), and the
 * server joins them with `joinSpoken` (apps/server/src/agent/live/call.ts);
 * the screen joins them the same way, so the words drawn while they arrive
 * are the words the turn lands with.
 */
import { joinSpoken, type LiveTranscript } from '@alluzo/shared';

/** What the person has said so far in this turn, once one more non-final fragment is in. */
export const hearingAfter = (held: string | null, fragment: string): string => joinSpoken([held ?? '', fragment]);

/**
 * What OS has said so far in this turn, once one more `said` frame is in: a
 * fragment joins on, and a final frame is the whole line and replaces them,
 * as the server's `saidWhole` does. An empty final keeps what came before.
 */
export const saidAfter = (held: string, message: { readonly text: string; readonly final: boolean }): string =>
  message.final && message.text.trim() !== '' ? message.text : joinSpoken([held, message.text]);

/**
 * Names one call's turns for `liveTurnMessages`, whose `n` must be unique
 * across every thread the messages may join. A thread outlives its calls (Type
 * instead, then Back to voice; Try again after a refused microphone), and each
 * call counts from one, so the count rides on a name for this call, as the
 * phone's `${mount}-${n}` does.
 */
export const turnNamer = (): (() => string) => {
  const call = crypto.randomUUID().slice(0, 8);
  let turns = 0;
  return () => {
    turns += 1;
    return `${call}-${turns}`;
  };
};

/**
 * What the person is saying (`hearing`) and what they said that OS has not
 * answered yet (`pending`), as the call's frames move them.
 */
export type PersonWords = { readonly hearing: string | null; readonly pending: string | null };

/**
 * The person's words once one more `heard` frame is in. A fragment adds to
 * what they are saying. A final frame hands their words to `pending`: its own
 * text when it has some, or the words heard so far when it is empty, as
 * Gemini's synthesised final at turn_complete is (gemini-live.ts).
 */
export const wordsOnHeard = (words: PersonWords, frame: { readonly text: string; readonly final: boolean }): PersonWords => {
  if (!frame.final) {
    if (frame.text.trim() === '') return words;
    return { hearing: hearingAfter(words.hearing, frame.text), pending: words.pending };
  }
  if (frame.text.trim() !== '') return { hearing: null, pending: frame.text };
  return { hearing: null, pending: words.hearing ?? words.pending };
};

/**
 * The person's words once OS begins to answer (its first `speaking` or `said`
 * frame): what they were saying is said, and waits on OS. Gemini marks nothing
 * final until the model has finished speaking (apps/server/src/agent/live/call.ts),
 * so this, not a final, is where the "You said" card and the working state begin.
 */
export const wordsOnAnswer = (words: PersonWords): PersonWords =>
  words.hearing === null ? words : { hearing: null, pending: words.hearing };

/**
 * On a version-2 assistant call, the person's words still arriving: their
 * latest line in the live transcript while it is partial, else null. The
 * server sends a line only once it is addressed, so side talk never shows.
 */
export const liveHearingOf = (transcript: LiveTranscript): string | null => {
  const last = transcript.rows.findLast((row) => row.role === 'you');
  if (last?.role !== 'you' || !last.partial || last.text.trim() === '') return null;
  return last.text;
};
